import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Download, Table2 } from "lucide-react";
import type { ExpectedDebitsBandDraft, ExpectedDebitsTable, IhqType } from "../types";
import { IHQ_TYPES } from "../types";
import { getApi } from "../api";
import { validateBandContiguity } from "../calc";
import { useAsyncResource } from "../hooks/useAsyncResource";
import { usePageSetup } from "../hooks/usePageSetup";
import { useShortcuts } from "../hooks/useShortcuts";
import { useCatalogStore } from "../store/catalogStore";
import { notify } from "../store/uiStore";
import { reportError } from "../services/eventBus";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { Alert } from "../components/Alert";
import { Button } from "../components/Button";
import { FieldRow } from "../components/FieldRow";
import { ErrorState, SkeletonForm } from "../components/ScreenStates";
import { ButtonGroupInput, FileUpload, TextArea, TextInput } from "../components/fields";
import { ExpectedDebitsEditor } from "../components/actuarial/ExpectedDebitsEditor";
import { RELATION_CLASS_LABELS } from "../utils/labels";
import { downloadExpectedDebitsTemplate, parseExpectedDebitsWorkbook } from "../utils/workbooks";

const IHQ_OPTIONS = IHQ_TYPES.map((value) => ({ value, label: value }));

type CompleteBand = ExpectedDebitsBandDraft & Record<Exclude<keyof ExpectedDebitsBandDraft, "relationClass">, number>;

function isComplete(band: ExpectedDebitsBandDraft): band is CompleteBand {
  return Object.values(band).every((value) => value !== null);
}

interface ExpectedDebitsTableFormProps {
  source: ExpectedDebitsTable | null;
}

function ExpectedDebitsTableForm({ source }: ExpectedDebitsTableFormProps) {
  const navigate = useNavigate();
  const setExpectedTables = useCatalogStore((state) => state.setExpectedTables);
  const [name, setName] = useState(() => (source ? `${source.name} copy` : ""));
  const [notes, setNotes] = useState(() => source?.notes ?? "");
  const [ihqType, setIhqType] = useState<IhqType | null>(() => source?.ihqType ?? null);
  const [bands, setBands] = useState<readonly ExpectedDebitsBandDraft[]>(() => source?.bands.map(({ id: _id, ...band }) => band) ?? []);
  const [file, setFile] = useState<File | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const incomplete = bands.filter((band) => !isComplete(band)).length;
  const issues = useMemo(() => validateBandContiguity(bands.filter(isComplete)), [bands]);
  const isValid = name.trim() !== "" && ihqType !== null && bands.length > 0 && incomplete === 0 && issues.length === 0;

  const handleFile = async (next: File | null) => {
    setFile(next);
    if (!next) return;
    setIsParsing(true);
    try {
      const parsed = await parseExpectedDebitsWorkbook(next);
      if (parsed.length === 0) notify("No Applicant, Spouse, or Child sheets were found in the workbook.", "warning");
      else {
        setBands(parsed);
        notify(`Loaded ${parsed.length} bands from ${next.name}.`, "success");
      }
    } catch (error) {
      reportError(error, "NewExpectedDebitsTablePage.parse");
    } finally {
      setIsParsing(false);
    }
  };

  const handleSave = useCallback(async () => {
    setNameError(name.trim() === "" ? "Table name is required" : null);
    if (!isValid || !ihqType) return;
    setIsSaving(true);
    try {
      const created = await getApi().createExpectedDebitTable({ name: name.trim(), notes: notes.trim() || null, ihqType, sourceTableId: source?.id ?? null, bands });
      setExpectedTables(await getApi().listExpectedDebitTables());
      notify(`Saved ${created.name}. Activate it from the list when ready.`, "success");
      navigate("/uw/actuarial/expected-debits");
    } catch (error) {
      reportError(error, "NewExpectedDebitsTablePage.save");
    } finally {
      setIsSaving(false);
    }
  }, [name, isValid, ihqType, notes, source, bands, setExpectedTables, navigate]);

  const handleCancel = useCallback(() => navigate("/uw/actuarial/expected-debits"), [navigate]);
  const shortcuts = useMemo(() => ({ save: () => void handleSave(), cancel: handleCancel }), [handleSave, handleCancel]);
  useShortcuts(shortcuts);

  return (
    <div className="space-y-5">
      <Card title="Table" icon={Table2}>
        <div className="space-y-4">
          <FieldRow>
            <TextInput id="table-name" label="Name" value={name} onChange={setName} error={nameError} required />
            <ButtonGroupInput id="table-form" label="Form Type" value={ihqType} onChange={setIhqType} options={IHQ_OPTIONS} required />
          </FieldRow>
          <TextArea id="table-notes" label="Notes" value={notes} onChange={setNotes} rows={2} />
          <FileUpload id="table-file" label="Upload from Excel (optional)" acceptExtensions={["xlsx"]} file={file} onFile={(next) => void handleFile(next)} isProcessing={isParsing} hint="Sheets named Employee (or Applicant), Spouse, and Child with the eight band columns." />
          <Button variant="ghost" size="sm" icon={Download} onClick={downloadExpectedDebitsTemplate}>
            Download template
          </Button>
        </div>
      </Card>
      <Card title="Age Bands" ariaLabel="Age Bands">
        <ExpectedDebitsEditor bands={bands} onChange={setBands} />
      </Card>
      <Card title="Validation">
        {bands.length === 0 && <Alert type="info" message="Enter at least one band for each relation class." />}
        {incomplete > 0 && <Alert type="warning" message={`${incomplete} band${incomplete === 1 ? " is" : "s are"} missing values.`} className="mb-2" />}
        {issues.length > 0 && (
          <ul className="space-y-1">
            {issues.map((issue, index) => (
              <li key={`${issue.relationClass}-${index}`}>
                <Alert type="error" message={`${RELATION_CLASS_LABELS[issue.relationClass]}: ${issue.message}`} />
              </li>
            ))}
          </ul>
        )}
        {bands.length > 0 && incomplete === 0 && issues.length === 0 && <Alert type="success" message="Bands are contiguous from 0 with no overlaps." />}
      </Card>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={handleCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={() => void handleSave()} disabled={!isValid} isLoading={isSaving}>
          Save Table
        </Button>
      </div>
    </div>
  );
}

export function NewExpectedDebitsTablePage() {
  usePageSetup("New Expected Debits Table", null);
  const [params] = useSearchParams();
  const copyOf = params.get("copyOf");
  const source = useAsyncResource(async () => (copyOf ? getApi().getExpectedDebitTable(copyOf) : null), copyOf ?? "new", "NewExpectedDebitsTablePage.source");

  if (source.state.kind === "error") return <ErrorState message={source.state.message} onRetry={source.reload} />;
  if (copyOf && source.state.kind === "loading") return <SkeletonForm fields={6} />;

  return (
    <>
      <PageHeader title={copyOf ? "Copy Expected Debits Table" : "New Expected Debits Table"} subtitle="Employee, Spouse, and Child bands must start at 0, stay contiguous, and never overlap. Saved tables are immutable; copy one to change it." />
      <ExpectedDebitsTableForm key={copyOf ?? "new"} source={source.data} />
    </>
  );
}
